import React, { useContext, useEffect, useImperativeHandle, useRef, useState } from 'react';
import {
  AiPriceAlertOutDTO,
  AiPriceAlertSeverityEnum,
  AiPriceAlertTypeEnum,
  ApiTypeEnum,
  CarrierQuoteStateEnum,
  CustomerQuoteStateEnum,
  EmailStatusEnum,
  EntityTypeEnum,
  ExpeditionQuoteCarrierFilterInDTO,
  ExpeditionStatusEnum,
  FeaturesEnum,
  Pageable,
  PermissionTypesEnum,
  PriceDTO,
  QuoteGroupEnum,
  QuotesPreviewOutDTO,
} from '@api/ringil-api.v2';

import { RowWithGap } from '@components/styles.tsx';
import LinkWithIcon from '@components/ringil3/Links/LinkWithIcon';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import {
  dayjsFromApiPrague,
  dayjsFromApiUtc,
  dayjsFromApiUtcInTz,
  formatDateFromDayjsWithLocale,
  formatDateTimeFromDayjsInTzWithLocale,
  formatTimeIntervalFromDayjsIntervalWithLocale,
  stringDateTimeWithLocaleFromApiStringInTz,
} from '@utils/dateWithTz';
import {
  enc,
  formatCarrierAdditionInfo,
  isNotNullOrUndefined,
  isNullOrUndefined,
  sortCarrierAdditionalInfosByDateOnly,
  useLocalStorage,
} from '@utils/utils';
import dayjs, { Dayjs } from 'dayjs';
import styled, { css } from 'styled-components';
import COBadge from '@components/FigmaComponents/COBadge.tsx';
import { Ringil3Note } from '@components/ringil3/styles';
import SvgMessage from '@components/ringil3/icons/Message';
import SvgWarning from '@components/ringil3/icons/Warning';
import { AuthenticatedUserContext } from '@components/auth/AuthenticatedUser.tsx';
import { useSecuredFlag } from '@components/security/useSecuredFlag';
import SecuredArea from '@components/security/SecuredArea';
import { SecuredFlag } from '@components/security/types';
import { useCreateAcceptQuote } from '@components/ringil3/Features/Expedition/QuoteTable/AcceptQuote/AcceptQuote.tsx';
import CustomerChatComponent from '@components/obsolete/Chat/CustomerChatComponent.tsx';
import { useCreateRejectQuote } from '@components/ringil3/Features/Expedition/QuoteTable/RejectQuote/RejectQuote.tsx';
import CalendarIcon from '@components/ringil3/icons/Calendar';
import { AiPriceAlertButton } from '@components/ai/AiPriceAlertButton.tsx';
import { getSeverityClassName } from '@components/ai/aiPriceAlertSeverity.ts';
import SvgRobot from '@icons/ai-robot.svg?react';
import SvgSev0 from '@icons/price-alert-sev0-inverted.svg?react';
import SvgSev1 from '@icons/price-alert-sev1-inverted.svg?react';
import SvgSev2 from '@icons/price-alert-sev2-inverted.svg?react';
import SvgSev3 from '@icons/price-alert-sev3-inverted.svg?react';
import SvgSev4 from '@icons/price-alert-sev4-inverted.svg?react';
import SvgClose from '@icons/close.svg?react';
import ExpandableLabelWrapper from '@components/RingilDataTable/ExpandableLabelWrapper.tsx';
import ExpandableInfoTag from '@components/RingilDataTable/ExpandableInfoTag.tsx';
import clsx from 'clsx';
import SvgChevron from '@icons/chevron.svg?react';
import SvgCarrierGroups from '@icons/grid-horizontal-inverted.svg?react';
import SvgLayers from '@icons/layers-inverted.svg?react';
import SvgQuoteNote from '@icons/note.svg?react';
import SvgAlert from '@icons/alert.svg?react';
import ExpandableInfoAllTag from '@components/RingilDataTable/ExpandableInfoAllTag.tsx';
import { getTransportIcon, joinLegInfo } from '@app/pages/carrier/demand-detail/carrierQuoteUtils.tsx';
import RingilButton from '@components/ringil3/Buttons/RingilButton.tsx';
import { formatPrice } from '@utils/priceUtils.ts';
import PriceTag from '@components/RingilComponents/tags/priceTag/PriceTag.tsx';
import FedexDisclaimer from '@components/RingilComponents/FedexDisclaimer.tsx';
import FedexLogo from '@components/RingilComponents/FedexLogo.tsx';
import ManipulationUnitPriceTag from '@components/RingilComponents/tags/priceTag/ManipulationUnitPriceTag.tsx';
import {
  useSchenkerQuoteAdditionalSidebar,
} from '@components/RingilComponents/quoteAdditionalInformation/SchenkerQuoteAdditionalInfoSidebar.tsx';
import COTimezone from '@components/FigmaComponents/COTimezone.tsx';
import {
  useDHLQuoteAdditionalSidebar,
} from '@components/RingilComponents/quoteAdditionalInformation/DHLQuoteAdditionalInfoSidebar.tsx';
import {
  useFedexQuoteAdditionalSidebar,
} from '@components/RingilComponents/quoteAdditionalInformation/FedexQuoteAdditionalInfoSidebar.tsx';
import { ApiContext } from '@api/api.tsx';
import RingilDataTable, {
  ColumnConfig,
  FilterConfig,
  FilterType,
  MassActionConfig,
  PagedData,
} from '@components/RingilDataTable/RingilDataTable.tsx';
import {
  useCreateCarrierTiebreak,
} from '@components/ringil3/Features/Expedition/QuoteTable/CarrierTiebreak/CarrierTiebreakSidebar.tsx';
import SvgInfo from '@icons/info.svg?react';
import { default as RingilDataTableOld } from '@components/ringil3/RingilDataTable/RingilDataTable';

import { toTestIdSlug } from '@utils/testUtils';

enum ExpandedRowType {
  Timeslot = 'timeslot',
  AiPriceAlert = 'aiPriceAlert',
}

class EnhancedQuote {
  id: string;
  quote: QuotesPreviewOutDTO;
  pricePerManipulationUnit: number | null;
  pricePerKm: number | null;
  totalPrice: number | null;
  totalPriceDto: PriceDTO;
  pricePerKmDto: PriceDTO;
  pricePerManipulationUnitDto: PriceDTO;
  freeManipulationUnits: number | null;
  expandedRowType?: ExpandedRowType;

  constructor(quote: QuotesPreviewOutDTO, ladenFactor: number, freeManipulationUnits: number) {
    this.id = quote.id;
    this.quote = quote;
    this.totalPrice = quote.offeredPrice && quote.offeredPrice !== 0 ? quote.offeredPrice : null;
    this.totalPriceDto = quote.priceDTO;
    this.freeManipulationUnits = freeManipulationUnits;
    this.pricePerManipulationUnit =
      quote.offeredPrice && quote.offeredManipulationUnits && quote.offeredPrice !== 0 ? quote.offeredPrice / quote.offeredManipulationUnits : null;
    this.pricePerKm =
      quote.offeredPrice && quote.distance && quote.distance > 0 && quote.offeredPrice !== 0
        ? ladenFactor && ladenFactor > 0 && quote.offeredManipulationUnits && quote.offeredManipulationUnits > 0
          ? (quote.offeredPrice * ladenFactor) / (quote.distance * quote.offeredManipulationUnits)
          : quote.offeredPrice / quote.distance
        : null;
    this.pricePerKmDto = quote.offeredPrice && quote.distance && quote.distance > 0 && quote.offeredPrice !== 0
      ? {
        ...quote.priceDTO,
        price: ladenFactor && ladenFactor > 0 && quote.offeredManipulationUnits && quote.offeredManipulationUnits > 0
          ? (quote.priceDTO.price * ladenFactor) / (quote.distance * quote.offeredManipulationUnits)
          : quote.priceDTO.price / quote.distance,
        companyComputedPrice: ladenFactor && ladenFactor > 0 && quote.offeredManipulationUnits && quote.offeredManipulationUnits > 0
          ? (quote.priceDTO.companyComputedPrice * ladenFactor) / (quote.distance * quote.offeredManipulationUnits)
          : quote.priceDTO.companyComputedPrice / quote.distance,
      }
      : null;
    this.pricePerManipulationUnitDto = quote.offeredPrice && quote.offeredManipulationUnits && quote.offeredPrice !== 0
      ? {
        ...quote.priceDTO,
        price: quote.priceDTO.price / quote.offeredManipulationUnits,
        companyComputedPrice: quote.priceDTO.companyComputedPrice / quote.offeredManipulationUnits,
      }
      : null;
  }

  toString(): string {
    return '';
  }

  static comparePriceDTO(aDto?: PriceDTO | null, bDto?: PriceDTO | null): number {
    if (!aDto || !bDto) {
      if (!aDto && !bDto) return 0;
      return !aDto ? 1 : -1;
    }

    if (aDto.price === null || aDto.companyComputedPrice === null) return 1;
    if (bDto.price === null || bDto.companyComputedPrice === null) return -1;

    if (aDto.companyComputedCurrency && bDto.companyComputedCurrency && aDto.companyComputedCurrency === bDto.companyComputedCurrency) {
      return aDto.companyComputedPrice - bDto.companyComputedPrice;
    } else if (aDto.currency && bDto.companyComputedCurrency && aDto.currency === bDto.companyComputedCurrency) {
      return aDto.price - bDto.companyComputedPrice;
    } else if (aDto.companyComputedCurrency && bDto.currency && aDto.companyComputedCurrency === bDto.currency) {
      return aDto.companyComputedPrice - bDto.price;
    }

    return aDto.price - bDto.price;
  }

  static sortByTotalPrice(a: EnhancedQuote, b: EnhancedQuote): number {
    return EnhancedQuote.comparePriceDTO(a.totalPriceDto, b.totalPriceDto);
  }

  static sortByPricePerManipulationUnit(a: EnhancedQuote, b: EnhancedQuote): number {
    return EnhancedQuote.comparePriceDTO(a.pricePerManipulationUnitDto, b.pricePerManipulationUnitDto);
  }

  static sortByPricePerKm(a: EnhancedQuote, b: EnhancedQuote): number {
    return EnhancedQuote.comparePriceDTO(a.pricePerKmDto, b.pricePerKmDto);
  }
}

enum InfoTagEnum {
  ALL = 'all',
  CARRIER_GROUPS = 'carrier_groups',
  ADDITIONAL_INFO = 'additional_info',
  TRANSPORT_LEGS = 'transport_legs',
  QUOTE_DEMAND_NOTE = 'quote_demand_note',
}

interface ComponentProps {
  onChanged: () => void;
  companyId: string;
  expeditionId: string;
  distributionId: string;
  showPrices: SecuredFlag;
  showMessages: SecuredFlag;
  editMessages: SecuredFlag;
  editTransportationFlag: SecuredFlag;
  transportationEditableFlag: SecuredFlag;
  ladenFactor?: number;
  freeManipulationUnits?: number;
  expeditionStatus: ExpeditionStatusEnum;
  distributionUtcEnd?: string;
  activeDistribution?: boolean;
}

export interface SpotQuoteTableHandle {
  reloadData: () => void;
}

const SpotQuoteTable = React.forwardRef<SpotQuoteTableHandle, ComponentProps>(
  ({ ...props }, ref) => {

    const {
      onChanged,
      companyId,
      expeditionId,
      distributionId,
      showPrices: showPricesFlag,
      editMessages: editMessagesFlag,
      showMessages: showMessagesFlag,
      editTransportationFlag,
      transportationEditableFlag,
      ladenFactor,
      freeManipulationUnits,
      expeditionStatus,
      distributionUtcEnd,
      activeDistribution = false,
    } = props;
    const editMessages = editMessagesFlag.granted;
    const showPrices = showPricesFlag.granted;
    const { tr } = useTranslationLgs();
    const { loggedUser } = useContext(AuthenticatedUserContext);
    const { getQuotesDbPaged } = useContext(ApiContext);
    const [selectedCarrierId, setSelectedCarrierId] = useState(undefined);
    const [isLabelExpanded, setIsLabelExpanded] = useState<boolean>(false);

    const carrierQuoteSelectionEnabled = (ladenFactor > 0 && freeManipulationUnits > 0) || (ladenFactor === 0 || isNullOrUndefined(ladenFactor));

    const loadDataApi = (filter: ExpeditionQuoteCarrierFilterInDTO, pageable: Pageable, onSuccess: (pagedData: PagedData<EnhancedQuote>) => void) => {
      getQuotesDbPaged(
        expeditionId,
        distributionId,
        filter,
        {
          ...pageable,
          pageNumber: pageable.pageNumber - 1,
        },
        response => {
          if (!response) {
            onSuccess({ data: [], total: 0, page: 1, size: pageable.pageSize } as PagedData<EnhancedQuote>);
            return;
          }

          const apiData = (response as any).data || [];
          const enhancedData: EnhancedQuote[] = apiData.map((q: QuotesPreviewOutDTO) => new EnhancedQuote(q, ladenFactor ?? 0, freeManipulationUnits ?? 0));

          const paged: PagedData<EnhancedQuote> = {
            data: enhancedData,
            total: (response as any).total,
            page: (response as any).page + 1,
            size: (response as any).size,
          };

          onSuccess(paged);
        },
      );
    };

    const tableRef = useRef<{ reloadData: () => void; selectedObjects: EnhancedQuote[]; clear: () => void }>(null);

    useImperativeHandle(ref, () => ({
      reloadData: () => tableRef?.current?.reloadData(),
    }));

    useEffect(() => {
      tableRef?.current?.reloadData();
    }, [distributionId, freeManipulationUnits]);

    const initialExpandedInfoTags: Record<InfoTagEnum, boolean> = Object.values(InfoTagEnum).reduce(
      (acc, key) => ({ ...acc, [key]: false }),
      {} as Record<InfoTagEnum, boolean>,
    );

    const [expandedInfoTags, setExpandedInfoTags] = useLocalStorage<Record<InfoTagEnum, boolean>>(
      'QUOTE_LIST_EXPANDED_INFO_TAGS',
      initialExpandedInfoTags,
    );


    const aiPriceFlag = useSecuredFlag('spot-ai-price-alert', {
      kind: 'visibility',
      description: 'AI price alert button (spot quotes)',
      permissions: [PermissionTypesEnum.PERMISSION_AI_PRICECHECK],
      features: [FeaturesEnum.AI_PRICE_ALERT],
    });
    const pricePerKmFlag = useSecuredFlag('spot-price-per-km', {
      kind: 'visibility',
      description: 'Price per km (spot quotes)',
      features: [FeaturesEnum.SHOW_PRICE_PER_KM],
    });
    const pricePerHuFlag = useSecuredFlag('spot-price-per-hu', {
      kind: 'visibility',
      description: 'Price per handling unit (spot quotes)',
      features: [FeaturesEnum.SHOW_PRICE_PER_HU],
    });
    const tiebreakFlag = useSecuredFlag('spot-carrier-tiebreak', {
      kind: 'action',
      description: 'Carrier tiebreak mass action (spot quotes)',
      features: [FeaturesEnum.DISTRIBUTION_CARRIER_TIEBREAK],
    });
    const showPricePerKm = pricePerKmFlag.granted;

    const renderName = (row: EnhancedQuote) => {
    const slug = toTestIdSlug(row.quote.name);
      return <>
        <div data-testid={slug ? `carrierSelection-carrier-${slug}-name` : undefined}>{row.quote.name}</div>
        <ExpandableLabelWrapper
          labels={row.quote.labels.map((label) => ({
            labelValue: label.labelValue,
            labelColor: label.labelColor,
          }))}
          isExpanded={isLabelExpanded}
          onToggle={() => setIsLabelExpanded(!isLabelExpanded)}
        />
      </>;
    };

    const { AcceptQuote, setSidebarData } = useCreateAcceptQuote();
    const { RejectQuote, setSidebarData: setRejectSidebarData } = useCreateRejectQuote();
    const {
      CarrierTiebreakSidebar,
      setSidebarData: setCarrierTiebreakSidebarData,
    } = useCreateCarrierTiebreak();
    const {
      SchenkerQuoteAdditionalSidebar,
      setSidebarData: setSchenkerSidebarData,
    } = useSchenkerQuoteAdditionalSidebar();

    function handleSchenkerAdditionalData(row: EnhancedQuote, isEditable: boolean = true) {
      setSchenkerSidebarData({
        quoteDemandId: row.quote.id,
        expeditionApplicationId: row.quote.expeditionApplicationId,
        carrierName: row.quote.carrierName,
        data: row.quote.additionalQuoteData.data,
        schenkerAddonsType: row.quote.schenkerAddonsType,
        isEditable: isEditable,
        isDeletable: expeditionStatus === ExpeditionStatusEnum.ASSIGNING,
        onContinue: () => setSidebarData({
          selectedQuoteId: row.quote.id,
          selectedCarrierName: row.quote.name,
          applicationId: row.quote.expeditionApplicationId,
          companyId: loggedUser.companyId,
          ladenFactor: ladenFactor,
          offeredManipulationUnits: row.quote.offeredManipulationUnits,
          freeManipulationUnits: row.freeManipulationUnits,
          price: row.quote.offeredPrice,
          currency: row.quote.currency,
        }),
        reloadData: onChanged,
      });
    }

    const {
      DHLQuoteAdditionalSidebar,
      setSidebarData: setDHLSidebarData,
    } = useDHLQuoteAdditionalSidebar();

    function handleDHLAdditionalData(row: EnhancedQuote, isEditable: boolean = true) {
      setDHLSidebarData({
        quoteDemandId: row.quote.id,
        expeditionApplicationId: row.quote.expeditionApplicationId,
        carrierName: row.quote.carrierName,
        data: row.quote.additionalQuoteData?.data,
        isEditable: isEditable,
        isDeletable: expeditionStatus === ExpeditionStatusEnum.ASSIGNING,
        onContinue: () => setSidebarData({
          selectedQuoteId: row.quote.id,
          selectedCarrierName: row.quote.name,
          applicationId: row.quote.expeditionApplicationId,
          companyId: loggedUser.companyId,
          ladenFactor: ladenFactor,
          offeredManipulationUnits: row.quote.offeredManipulationUnits,
          freeManipulationUnits: row.freeManipulationUnits,
          price: row.quote.offeredPrice,
          currency: row.quote.currency,
        }),
        reloadData: onChanged,
      });
    }

    const {
      FedexQuoteAdditionalSidebar,
      setSidebarData: setFedexSidebarData,
    } = useFedexQuoteAdditionalSidebar();

    function handleFedexAdditionalData(row: EnhancedQuote, isEditable: boolean = true) {
      setFedexSidebarData({
        quoteDemandId: row.quote.id,
        expeditionApplicationId: row.quote.expeditionApplicationId,
        carrierName: row.quote.carrierName,
        data: row.quote.additionalQuoteData?.data,
        isEditable: isEditable,
        isDeletable: expeditionStatus === ExpeditionStatusEnum.ASSIGNING,
        onContinue: () => setSidebarData({
          selectedQuoteId: row.quote.id,
          selectedCarrierName: row.quote.name,
          applicationId: row.quote.expeditionApplicationId,
          companyId: loggedUser.companyId,
          ladenFactor: ladenFactor,
          offeredManipulationUnits: row.quote.offeredManipulationUnits,
          freeManipulationUnits: row.freeManipulationUnits,
          price: row.quote.offeredPrice,
          currency: row.quote.currency,
        }),
        reloadData: onChanged,
      });
    }

    const handlePickButton = (row: EnhancedQuote) => {

      if (row.quote.additionalQuoteData && isNullOrUndefined(row.quote.additionalQuoteData.data)) {
        switch (row.quote.additionalQuoteData.apiType) {
          case ApiTypeEnum.SCHENKER:
            handleSchenkerAdditionalData(row);
            break;
          case ApiTypeEnum.DHL:
            handleDHLAdditionalData(row);
            break;
          case ApiTypeEnum.FEDEX:
            handleFedexAdditionalData(row);
            break;
        }
      } else {
        setSidebarData({
          selectedQuoteId: row.quote.id,
          selectedCarrierName: row.quote.name,
          applicationId: row.quote.expeditionApplicationId,
          companyId: loggedUser.companyId,
          ladenFactor: ladenFactor,
          offeredManipulationUnits: row.quote.offeredManipulationUnits,
          freeManipulationUnits: row.freeManipulationUnits,
          price: row.quote.offeredPrice,
          currency: row.quote.currency,
        });
      }
    };

    const renderActions = (row: EnhancedQuote) => {
      return (
        <>
          <SecuredArea
            id="spot-action-write-message" instance={row.quote.id}
            label="Write message"
            description="Start a conversation with the carrier about its quote"
            visible={[
              editMessagesFlag,
              { type: 'data', label: 'messagesCount == 0', satisfied: row.quote.messagesCount == 0 },
            ]}
          >
            <LinkWithIcon
              onClick={() => setSelectedCarrierId(row.quote.carrierId)}
              icon={<SvgMessage />}
              label={tr(`QuoteTable.writeMessage`, `Write message`)}
              dataTestId={`expedition-quote-messages-${row.quote.id}`}
            />
          </SecuredArea>
          <SecuredArea
            id="spot-action-messages" instance={row.quote.id}
            label="Messages reply"
            description="Open the messages exchanged with the carrier about its quote"
            visible={[
              showMessagesFlag,
              { type: 'data', label: 'messagesCount > 0', satisfied: row.quote.messagesCount > 0 },
            ]}
          >
            <LinkWithIcon
              onClick={() => setSelectedCarrierId(row.quote.carrierId)}
              label={tr(`QuoteTable.messagesReply`, `Message: {{messagesCount}}`, {
                messagesCount: row.quote.messagesCount,
              })}
              dataTestId={`expedition-quote-messages-${row.quote.id}`}
            />
          </SecuredArea>
          <SecuredArea
            id="spot-action-select" instance={row.quote.id}
            label="Pick / Reject carrier quote"
            description="Pick or reject the carrier's quote"
            visible={[
              editTransportationFlag,
              transportationEditableFlag,
              {
                type: 'data',
                label: 'quoteGroup RINGIL/DACHSER',
                satisfied: row.quote.quoteGroup === QuoteGroupEnum.RINGIL || row.quote.quoteGroup == QuoteGroupEnum.DACHSER,
              },
              {
                // A capacity state of the shipment, not "nothing to show": as `data` it would never reach the AI assistant.
                type: 'state',
                label: 'carrierQuoteSelectionEnabled',
                satisfied: carrierQuoteSelectionEnabled,
                description: 'With a laden factor set, carriers can be picked only while the shipment still has free handling units',
                byDesign: true,
              },
            ]}
          >
              <div className={'mt-4'}>
                <SecuredArea
                  id="spot-action-pick-reject" instance={row.quote.id}
                  label="Pick / Reject buttons"
                  visible={[
                    {
                      type: 'data',
                      label: 'carrierQuoteState == OFFERED_PRICE',
                      satisfied: row.quote.carrierQuoteState === CarrierQuoteStateEnum.OFFERED_PRICE,
                    },
                    { type: 'data', label: 'not expired', satisfied: !row.quote.isExpired },
                    {
                      type: 'data',
                      label: 'enough remaining units for one laden factor',
                      satisfied: isNullOrUndefined(ladenFactor) || ladenFactor <= 0 || freeManipulationUnits >= ladenFactor,
                    },
                  ]}
                >
                  <div className="flex flex-col gap-2">
                    <RingilButton
                      mode={'filled-rectangle'}
                      label={tr('customerExpeditionDetail.tabCarrierPending.pickButton', 'Vybrat')}
                      dataTestId={`expedition-quote-pick-${row.quote.id}`}
                      onClick={() => handlePickButton(row)}
                      secured={{
                        id: 'spot-action-pick', instance: row.quote.id,
                        label: 'Pick carrier quote',
                        editable: [{ type: 'data', label: 'not in tiebreak', satisfied: !row.quote.isTieBreaker }],
                      }}
                    />
                    <RingilButton
                      mode={'outlined-rectangle'}
                      label={tr('customerExpeditionDetail.tabCarrierPending.rejectButton', 'Odmítnout')}
                      dataTestId={`expedition-quote-reject-${row.quote.id}`}
                      onClick={() => {
                        setRejectSidebarData({
                          selectedQuoteId: row.quote.id,
                          selectedCarrierName: row.quote.name,
                          applicationId: row.quote.expeditionApplicationId,
                          companyId: loggedUser.companyId,
                        });
                      }}
                      secured={{
                        id: 'spot-action-reject', instance: row.quote.id,
                        label: 'Reject carrier quote',
                        editable: [{ type: 'data', label: 'not in tiebreak', satisfied: !row.quote.isTieBreaker }],
                      }}
                    />
                  </div>
                </SecuredArea>
                <SecuredArea
                  id="spot-action-accepted-units" instance={row.quote.id}
                  label="Accepted units label"
                  visible={[
                    {
                      type: 'data',
                      label: 'carrierQuoteState == ACCEPTED_BY_CUSTOMER',
                      satisfied: row.quote.carrierQuoteState === CarrierQuoteStateEnum.ACCEPTED_BY_CUSTOMER,
                    },
                    { type: 'data', label: 'not expired', satisfied: !row.quote.isExpired },
                  ]}
                >
                  <GreyLabel>
                    {tr('SpotQuoteTable.acceptedUnits', 'Selected: {{chosenManipulationUnits}} MU', { chosenManipulationUnits: row.quote.chosenManipulationUnits })}
                  </GreyLabel>
                </SecuredArea>
              </div>
          </SecuredArea>
        </>
      );
    };

    const handleExpandRowToggle = (row: EnhancedQuote, expansionType: ExpandedRowType, toggleExpandedRow?: (row: EnhancedQuote) => void) => {
      if (isNullOrUndefined(row.expandedRowType)) {
        row.expandedRowType = expansionType;
        toggleExpandedRow(row);
      } else if (row.expandedRowType === expansionType) {
        row.expandedRowType = null;
        toggleExpandedRow(row);
      } else {
        toggleExpandedRow(row)
        row.expandedRowType = expansionType;
        toggleExpandedRow(row);
      }
    };

    const renderRowExpansion = (row: EnhancedQuote, toggleExpandedRow?: (row: EnhancedQuote) => void) => {
      switch (row.expandedRowType) {
        case ExpandedRowType.AiPriceAlert:
          return renderAiPriceExpansion(row, toggleExpandedRow);
        case ExpandedRowType.Timeslot:
          return renderTimeslotsExpansion(row);
        default:
          break;
      }
    };

    const displayableSeverities = [
      AiPriceAlertSeverityEnum.SEVERITY_0,
      AiPriceAlertSeverityEnum.SEVERITY_1,
      AiPriceAlertSeverityEnum.SEVERITY_2,
      AiPriceAlertSeverityEnum.SEVERITY_3,
      AiPriceAlertSeverityEnum.SEVERITY_4,
    ];

    function getDisplayableAiPriceAlert(alert?: AiPriceAlertOutDTO): AiPriceAlertOutDTO | undefined {
      if (
        alert?.aiPriceAlertType === AiPriceAlertTypeEnum.PRICE_DEVIATION_FROM_HISTORY &&
        displayableSeverities.includes(alert.aiPriceAlertSeverity) &&
        !isNullOrUndefined(alert.typicalPrice) &&
        !isNullOrUndefined(alert.typicalPriceCurrency)
      ) {
        return alert;
      }
      return undefined;
    }

    function getSeverityBorderColor(severity?: AiPriceAlertSeverityEnum) {
      switch (severity) {
        case AiPriceAlertSeverityEnum.SEVERITY_4:
          return 'border-complementary-error';
        case AiPriceAlertSeverityEnum.SEVERITY_3:
          return 'border-content-brand';
        case AiPriceAlertSeverityEnum.SEVERITY_2:
          return 'border-complementary-warning';
        case AiPriceAlertSeverityEnum.SEVERITY_1:
          return 'border-complementary-info';
        case AiPriceAlertSeverityEnum.SEVERITY_0:
          return 'border-complementary-success';
        default:
          return 'border-content-gr2';
      }
    }

    function getSeverityIcon(severity: AiPriceAlertSeverityEnum) {
      switch (severity) {
        case AiPriceAlertSeverityEnum.SEVERITY_4:
          return <SvgSev4 className={`w-6 h-6 text-complementary-error`} />;
        case AiPriceAlertSeverityEnum.SEVERITY_3:
          return <SvgSev3 className={`w-6 h-6 text-content-brand`} />;
        case AiPriceAlertSeverityEnum.SEVERITY_2:
          return <SvgSev2 className={`w-6 h-6 text-complementary-warning`} />;
        case AiPriceAlertSeverityEnum.SEVERITY_1:
          return <SvgSev1 className={`w-6 h-6 text-complementary-info`} />;
        case AiPriceAlertSeverityEnum.SEVERITY_0:
          return <SvgSev0 className={`w-6 h-6 text-complementary-success`} />;
        default:
          return <></>;
      }
    }

    function getSeverityTag(severity: AiPriceAlertSeverityEnum) {
      return (
        <div
          className={`flex px-2 py-1 justify-center items-center gap-2 rounded-lg border border-solid ${getSeverityClassName(severity)}`}>
          {getSeverityIcon(severity)}
          <span
            className="font-dm-sans font-bold text-[14px] leading-[12px] text-content-primary]"
          >
        {severity.split('_')[1]}
      </span>
        </div>
      );
    }

    const handleInfoTagClick = (key: InfoTagEnum) => {
      setExpandedInfoTags(prev => ({
        ...prev,
        [key]: !prev[key],
      }));
    };

    const setAllTagsExpanded = (expanded: boolean) => {
      setExpandedInfoTags(_ => {
        const allInfoTagKeys = Object.values(InfoTagEnum);
        const newState = {} as Record<InfoTagEnum, boolean>;
        allInfoTagKeys.forEach(key => {
          newState[key] = expanded;
        });
        return newState;
      });
    };

    const handleAllTagClick = (key: InfoTagEnum) => {
      setAllTagsExpanded(!expandedInfoTags[key]);
    };


    const renderAiPriceExpansion = (row: EnhancedQuote, toggleExpandedRow?: (row: EnhancedQuote) => void) => {
      const alert = getDisplayableAiPriceAlert(row.quote.aiPriceAlert);

      return (
        <div
          className={`flex flex-col w-[500px] p-4 items-start gap-4 rounded-lg border-[3px] border-solid bg-[#F7F7F7] mx-auto  ${getSeverityBorderColor(alert?.aiPriceAlertSeverity)} `}
        >
          <div className="flex flex-row items-center justify-between w-full">
            <div className={'flex gap-2'}>
              <SvgRobot className="w-6 h-6" />
              <span
                className="font-dm-sans text-base font-medium text-content-primary tracking-tight"
              >
                {tr('SpotQuoteTable.aiPriceAlertTitle', 'AI Price Alert')}
            </span>
            </div>
            <SvgClose
              className="w-6 h-6 text-content-brand"
              onClick={() => {
                handleExpandRowToggle(row, ExpandedRowType.AiPriceAlert, toggleExpandedRow)
              }}
            />
          </div>
          {alert ? (
            <div className="flex flex-row items-center gap-2">
              {getSeverityTag(alert.aiPriceAlertSeverity)}
              <span>
                {tr('SpotQuoteTable.aiPriceAlertTypicalPrice', 'Typical price: {{typicalPrice}}', {
                  typicalPrice: formatPrice(alert.typicalPrice, alert.typicalPriceCurrency),
                })}
              </span>
            </div>
          ) : (
            <span className="text-sm text-gray-500">
              {tr('SpotQuoteTable.aiPriceAlertInsufficientData', 'Insufficient data')}
            </span>
          )}
          <div className="flex text-content-secondary font-dm-sans text-[8px] font-normal not-italic leading-[12px]">
            {tr('SpotQuoteTable.aiPriceAlertDetailDisclaimer', 'The AI classification is provided for informational purposes only and may not always be accurate.')}
          </div>
        </div>
      );
    };

  const renderTimeslotsExpansion = (row: EnhancedQuote) => {
    const timeslotData = row.quote.locations.map(location => {
      const carrierSuggestedFormattedSlots: string[] = [];
      const userFormattedTimeslots: string[] = [];
      let selectedSlot: string | null = null;

      location.timeslots.forEach(ts => {
        // Atomically check all new fields — only use new fields if utcStart, utcEnd and timezoneId are all present
        const useNewFields = !!(ts.utcStart && ts.utcEnd && ts.timezoneId);
        const effectiveTimezoneId = useNewFields ? ts.timezoneId : 'Europe/Prague';
        const sinceDate = useNewFields ? dayjsFromApiUtc(ts.utcStart) : dayjsFromApiPrague(ts.sinceInUTC);
        const tillDate = useNewFields ? dayjsFromApiUtc(ts.utcEnd) : dayjsFromApiPrague(ts.tillInUTC);

        const sinceInLocationTz = sinceDate.tz(effectiveTimezoneId);
        const tillInLocationTz = tillDate.tz(effectiveTimezoneId);
        const isSameDay = sinceInLocationTz.isSame(tillInLocationTz, 'day');

        let formattedSlot: string;
        if (isSameDay) {
          // Pokud jsou datumy stejné, použijeme původní formát
          const date = formatDateFromDayjsWithLocale(sinceInLocationTz);
          const time = formatTimeIntervalFromDayjsIntervalWithLocale(sinceInLocationTz, tillInLocationTz, effectiveTimezoneId);
          formattedSlot = `${date} | ${time}`;
        } else {
          // Pokud jsou na různé dny, zobrazíme plné datum a čas pro oba
          const sinceFormatted = formatDateTimeFromDayjsInTzWithLocale(sinceInLocationTz, effectiveTimezoneId);
          const tillFormatted = formatDateTimeFromDayjsInTzWithLocale(tillInLocationTz, effectiveTimezoneId);
          formattedSlot = `${sinceFormatted} - ${tillFormatted}`;
        }

        if (ts.isCarrier === true) {
          if (ts.isSelected === true) {
            selectedSlot = formattedSlot;
          } else {
            carrierSuggestedFormattedSlots.push(formattedSlot);
          }
        } else {
          userFormattedTimeslots.push(formattedSlot);
        }
      });

      // Put selected slot first in the array
      const finalCarrierSlots = selectedSlot
        ? [selectedSlot, ...carrierSuggestedFormattedSlots]
        : carrierSuggestedFormattedSlots;

      // Derive display timezoneId atomically from the first timeslot that has all new fields
      const firstTs = location.timeslots[0];
      const displayTimezoneId =
        firstTs?.utcStart && firstTs?.utcEnd && firstTs?.timezoneId
          ? firstTs.timezoneId
          : 'Europe/Prague';

      return {
        id: location.locationId,
        fullCityAddress: location.city,
        locationName: location.name,
        carrierSuggestedSlots: finalCarrierSlots,
        userSlots: userFormattedTimeslots,
        timezoneId: displayTimezoneId,
      };
    });

      const renderLocationDisplay = (locationRow: (typeof timeslotData)[0]) => (
        <div>
          <div style={{ fontWeight: '500' }}>{locationRow.fullCityAddress}</div>
          <div>{locationRow.locationName}</div>
        </div>
      );

    const renderCarrierSuggestedSlot = (locationRow: (typeof timeslotData)[0]) => {
      if (!locationRow.carrierSuggestedSlots || locationRow.carrierSuggestedSlots.length === 0) {
        return null;
      }
      return (
        <>
          <div>
            {locationRow.carrierSuggestedSlots.map((slot, index) => (
              <div
                key={index}
                style={{
                  color: index === 0 ? '#4A25AA' : '#9CA3AF',
                  display: 'flex',
                  alignItems: 'center',
                  marginBottom: index < locationRow.carrierSuggestedSlots.length - 1 ? '0.5rem' : '0'
                }}
              >
                <CalendarIcon fill={index === 0 ? '#4A25AA' : '#9CA3AF'} style={{ marginRight: '0.5rem' }} />
                {slot}
              </div>
            ))}
          </div>
          <COTimezone timezoneId={locationRow.timezoneId}/>
        </>
      );
    };

    const renderUserTimeslots = (locationRow: (typeof timeslotData)[0]) => (
      <div>
        {locationRow.userSlots.map((tsText, index) => (
          <div key={index} style={{ display: 'flex', alignItems: 'center' }}>
            <CalendarIcon fill="#616061" style={{ marginRight: '0.5rem' }} />
            {tsText}
          </div>
        ))}
        <COTimezone timezoneId={locationRow.timezoneId}/>
      </div>
    );

      return (
        <TimeslotsSubtable>
          {row.quote.locations.length > 0 ? (
            <>
              <div className="p-d-flex gap-2 p-ai-center ml-4 mb-4">
                <SvgWarning pathFill="#B2B2B2" />
                <StyledTimeslotHeader>
                  {tr(`SpotQuoteDataTable.companyProposedTimeslots`, 'Navrhovane sloty pro {{company}}', {
                    company: row.quote.name,
                  })}
                </StyledTimeslotHeader>
              </div>
              <RingilDataTableOld
                data={timeslotData}
                columns={[
                  { name: tr('SpotQuoteTable.subTable.location', 'On location') },
                  { name: tr('SpotQuoteTable.subTable.suggestedSlot', 'Suggested slot') },
                  { name: tr('SpotQuoteTable.subTable.userSlots', 'Your slots') },
                ]}
                renderers={[renderLocationDisplay, renderCarrierSuggestedSlot, renderUserTimeslots]}
              />
            </>
          ) : (
            <h5
              className="p-text-center no-slots">{tr(`SpotQuoteDataTable.notProposedTimeslots`, `Žádné časové sloty!`)}</h5>
          )}
        </TimeslotsSubtable>
      );
    };

    const renderAdditionalInfo = (row: EnhancedQuote, toggleExpandedRow?: (row: EnhancedQuote) => void) => {
      const hasDifferentSlot = row.quote.locations.filter(location => location.isDifferent).length > 0;
      const isAnyAdditionalInfoInAlert = row.quote.additionalInfo?.some(info => {
        const now = new Date();
        const from = info.from ? new Date(info.from) : null;
        const to = info.to ? new Date(info.to) : null;
        return (from && now < from) || (to && now > to);
      });

      const iaAdditionalInfoAvailable = (row.quote.carrierGroups && row.quote.carrierGroups.length > 0)
        || (row.quote.additionalInfo && row.quote.additionalInfo.length > 0);

      return (
        <SecuredArea
          id="spot-additional-info" instance={row.quote.id}
          label="Additional info"
          description="Additional information from the carrier's quote: carrier groups, notes, transport legs, API details"
          visible={showMessagesFlag}
        >
          <div className="flex flex-col">
            <div className="flex flex-wrap gap-2">
              <SecuredArea
                id="spot-additional-info-all-tag" instance={row.quote.id}
                label="Expand-all tag"
                visible={[{ type: 'data', label: 'has additional info', satisfied: iaAdditionalInfoAvailable }]}
              >
                <ExpandableInfoAllTag
                  id={`all_${enc(row.quote.id)}`}
                  onClick={() => handleAllTagClick(InfoTagEnum.ALL)}
                  tooltipText={expandedInfoTags['all'] ? tr(`expandableInfoTag.collapseAll`, 'Collapse all') : tr(`expandableInfoTag.expandAll`, 'Expand all')}
                  icon={
                    <SvgChevron className={clsx('text-content-brand',
                      expandedInfoTags['all'] && 'rotate-180')}
                    />
                  }
                />
              </SecuredArea>
              <SecuredArea
                id="spot-additional-info-carrier-groups" instance={row.quote.id}
                label="Carrier groups tag"
                visible={[{
                  type: 'data',
                  label: 'has carrierGroups',
                  satisfied: !!row.quote.carrierGroups && row.quote.carrierGroups.length > 0,
                }]}
              >
                <ExpandableInfoTag
                  id={`carrier_groups_${enc(row.quote.id)}`}
                  expanded={expandedInfoTags[InfoTagEnum.CARRIER_GROUPS]}
                  onClick={() => handleInfoTagClick(InfoTagEnum.CARRIER_GROUPS)}
                  icon={<SvgCarrierGroups />}
                  label={tr(`SpotQuoteDataTable.carrierGroups`, 'Carrier groups: ')}
                  text={row.quote.carrierGroups?.join(', ')}
                  count={row.quote.carrierGroups?.length}
                />
              </SecuredArea>
              <SecuredArea
                id="spot-additional-info-list" instance={row.quote.id}
                label="Additional info tag"
                visible={[{
                  type: 'data',
                  label: 'has additionalInfo',
                  satisfied: !!row.quote.additionalInfo && row.quote.additionalInfo.length > 0,
                }]}
              >
                <ExpandableInfoTag
                  id={`additional_info_${enc(row.quote.id)}`}
                  expanded={expandedInfoTags[InfoTagEnum.ADDITIONAL_INFO]}
                  onClick={() => handleInfoTagClick(InfoTagEnum.ADDITIONAL_INFO)}
                  icon={<SvgAlert className={clsx(isAnyAdditionalInfoInAlert ? 'text-complementary-error' : '')} />}
                  label={tr(`SpotQuoteDataTable.additionalInfo`, 'Additional info: ')}
                  text={
                    row.quote.additionalInfo
                      ?.sort(sortCarrierAdditionalInfosByDateOnly)
                      .map(i => {
                        const fromDate = i.from ? new Date(i.from).toLocaleDateString() : '';
                        const toDate = i.to ? new Date(i.to).toLocaleDateString() : '';
                        return `${i.text} ${fromDate}${toDate ? ` - ${toDate}` : ''}`;
                      })
                      .join(' | ')
                  }
                  textComponent={formatCarrierAdditionInfo(row.quote.additionalInfo)}
                  count={row.quote.additionalInfo?.length}
                />
              </SecuredArea>
              <SecuredArea
                id="spot-additional-info-transport-legs" instance={row.quote.id}
                label="Transport legs tag"
                visible={[{
                  type: 'data',
                  label: 'has transportLegs',
                  satisfied: !!row.quote.transportLegs && row.quote.transportLegs.length > 0,
                }]}
              >
                <ExpandableInfoTag
                  id={`transport_legs_${enc(row.quote.id)}`}
                  expanded={expandedInfoTags[InfoTagEnum.TRANSPORT_LEGS]}
                  onClick={() => handleInfoTagClick(InfoTagEnum.TRANSPORT_LEGS)}
                  icon={<SvgLayers />}
                  label={tr(`SpotQuoteDataTable.transportLegs`, 'Transport legs: ')}
                  text={
                    row.quote.transportLegs
                      ?.map(leg => joinLegInfo(leg))
                      .join(' | ')
                  }
                  textComponent={row.quote.transportLegs?.map((leg, idx) => (
                    <div key={idx}>
                      <div className="flex flex-row gap-2">
                        <div className="flex items-start justify-start">
                          {getTransportIcon(leg.transportationType, 3)}
                        </div>
                        <div className="flex flex-col text-content-primary font-dm-sans text-[14px] font-normal">
                          {tr('carrier.demand.quote.transportationType.' + leg.transportationType.toLowerCase() + '.prefix', `${leg.transportationType}: `)}
                          {joinLegInfo(leg)}
                        </div>
                      </div>
                    </div>
                  ))}
                  count={row.quote.transportLegs?.length}
                />
              </SecuredArea>
              <SecuredArea
                id="spot-additional-info-quote-demand-note" instance={row.quote.id}
                label="Quote demand note tag"
                visible={[{ type: 'data', label: 'has quoteDemandNote', satisfied: !!row.quote.quoteDemandNote }]}
              >
                <ExpandableInfoTag
                  id={`quote_demand_note_${enc(row.quote.id)}`}
                  expanded={expandedInfoTags[InfoTagEnum.QUOTE_DEMAND_NOTE]}
                  onClick={() => handleInfoTagClick(InfoTagEnum.QUOTE_DEMAND_NOTE)}
                  icon={<SvgQuoteNote />}
                  label={tr(`SpotQuoteDataTable.quoteDemandNote`, 'Quote demand note: ')}
                  text={row.quote.quoteDemandNote}
                />
              </SecuredArea>
            </div>
            <div className="flex flex-col justify-center gap-2">
              <SecuredArea
                id="spot-additional-info-dachser" instance={row.quote.id}
                label="Dachser parameters"
                visible={[{ type: 'data', label: 'has dachserProductType', satisfied: !!row.quote.dachserProductType }]}
              >
              <span>
            {tr(`SpotQuoteTable.additionalInfoParameters`, 'Parameters:')}
                {' '}
                {row.quote.dachserProductType}
                {row.quote.dachserDeliveryTermTranslated &&
                  ` | ${row.quote.dachserDeliveryTermTranslated}`}
          </span>
              </SecuredArea>
              <SecuredArea
                id="spot-additional-info-schenker" instance={row.quote.id}
                label="Schenker parameters"
                visible={[{
                  type: 'data',
                  label: 'has schenkerProductType',
                  satisfied: !!row.quote.schenkerProductType,
                }]}
              >
                <span>
                  {tr(`SpotQuoteTable.additionalInfoParameters`, 'Parameters:')}
                  {' '}
                  {row.quote.schenkerProductType}
                  {!isNullOrUndefined(row.quote.schenkerAddonsType) && row.quote.schenkerAddonsType.length > 0 && `| ${row.quote.schenkerAddonsType}`}
                </span>
              </SecuredArea>
              <SecuredArea
                id="spot-additional-info-dhl" instance={row.quote.id}
                label="DHL parameters"
                visible={[{ type: 'data', label: 'has dhlProductType', satisfied: !!row.quote.dhlProductType }]}
              >
                <span>
                  {tr(`SpotQuoteTable.additionalInfoParameters`, 'Parameters:')}
                  {' '}
                  {row.quote.dhlProductType}
                </span>
              </SecuredArea>
              <SecuredArea
                id="spot-additional-info-fedex" instance={row.quote.id}
                label="FedEx parameters"
                visible={[{ type: 'data', label: 'has fedexProductType', satisfied: !!row.quote.fedexProductType }]}
              >
                <span>
                  {tr(`SpotQuoteTable.additionalInfoParameters`, 'Parameters:')}
                  {' '}
                  {row.quote.fedexProductType}
                  {row.quote.fedexAccountNumber && (
                    <>
                      {', '}
                      {tr('SpotQuoteTable.fedexAccount', 'FedEx account')} {row.quote.fedexAccountNumber}
                    </>
                  )}
                </span>
              </SecuredArea>
              <SecuredArea
                id="spot-additional-info-different-slot" instance={row.quote.id}
                label="Different slots link"
                visible={[{ type: 'data', label: 'has different slot', satisfied: hasDifferentSlot }]}
              >
                <LinkWithIcon
                  label={tr(`SpotQuoteTable.diferrentSlots`, `Diferrent slots`)}
                  icon={<SvgWarning />}
                  iconRight={
                    <SvgChevron className={clsx('text-content-brand',
                      row.expandedRowType === ExpandedRowType.Timeslot && 'rotate-180')}
                    />
                  }
                  onClick={() => {
                    handleExpandRowToggle(row, ExpandedRowType.Timeslot, toggleExpandedRow);
                  }}
                />
              </SecuredArea>
              <SecuredArea
                id="spot-additional-info-additional-data" instance={row.quote.id}
                label="View additional data link"
                visible={[{
                  type: 'data',
                  label: 'has additionalQuoteData.data',
                  satisfied: !!row.quote.additionalQuoteData?.data,
                }]}
              >
                <LinkWithIcon
                  label={tr('SpotQuoteTable.viewAdditionalData', 'View additional data')}
                  onClick={() => {
                    switch (row.quote.additionalQuoteData?.apiType) {
                      case ApiTypeEnum.SCHENKER:
                        handleSchenkerAdditionalData(row, false);
                        break;
                      case ApiTypeEnum.DHL:
                        handleDHLAdditionalData(row, false);
                        break;
                      case ApiTypeEnum.FEDEX:
                        handleFedexAdditionalData(row, false);
                        break;
                    }
                  }}
                />
              </SecuredArea>
            </div>
            {row.quote.additionalQuoteData?.apiType === ApiTypeEnum.FEDEX && (
              <div className="flex flex-col gap-1 mt-2">
                <FedexLogo height={32} className="self-start" />
                <FedexDisclaimer />
              </div>
            )}
          </div>
        </SecuredArea>
      );
    };

    const mapToColor = (emailStatus?: EmailStatusEnum) => {
      switch (emailStatus) {
        case EmailStatusEnum.DELIVERED:
        case EmailStatusEnum.READ_EMAIL:
        case EmailStatusEnum.READ_IN_APP:
        case EmailStatusEnum.SEND:
          return 'var(--ringil3-complementary-draft)';
        case EmailStatusEnum.FAILED:
          return 'var(--ringil3-complementary-error-color)';
        default:
          return '';
      }
    };

    const mapCarrierQuoteStateToColor = (emailStatus?: CarrierQuoteStateEnum) => {
      switch (emailStatus) {
        case CarrierQuoteStateEnum.ACCEPTED_BY_CUSTOMER:
        case CarrierQuoteStateEnum.ACCEPTED_NEED_RECONFIRM:
          return 'var(--ringil3-complementary-success)';
        case CarrierQuoteStateEnum.OFFERED_PRICE:
          return 'var(--ringil3-complementary-draft)';
        case CarrierQuoteStateEnum.REJECTED_BY_CARRIER:
          return 'var(--ringil3-complementary-error-color)';
        case CarrierQuoteStateEnum.NEW:
        case CarrierQuoteStateEnum.REJECTED_BY_CUSTOMER:
        default:
          return '';
      }
    };

    const mapCustomerQuoteStateToColor = (emailStatus?: CustomerQuoteStateEnum) => {
      switch (emailStatus) {
        case CustomerQuoteStateEnum.ASSIGNED_MANUALLY_BY_CUSTOMER:
        case CustomerQuoteStateEnum.ACCEPTED_BY_CARRIER:
          return 'var(--ringil3-complementary-success)';
        case CustomerQuoteStateEnum.REJECTED_BY_CARRIER:
          return 'var(--ringil3-complementary-error-color)';
        default:
          return '';
      }
    };

    const renderState = (row: EnhancedQuote) => {
      const timezoneId = loggedUser.defaultTimezoneId ? row.quote.timezoneId : 'Europe/Prague';
      const carrierActionAtFlag = row.quote.carrierActionAtUtc || row.quote.carrierActionAt;
      const stateActionAt = row.quote.carrierQuoteState === CarrierQuoteStateEnum.REJECTED_BY_CUSTOMER
        ? row.quote.rejectedByCustomerAtUtc || row.quote.rejectedByCustomerAt
        : row.quote.carrierQuoteState === CarrierQuoteStateEnum.ACCEPTED_BY_CUSTOMER
          ? row.quote.acceptedAtUtc || row.quote.acceptedAt
          : row.quote.carrierActionAtUtc || row.quote.carrierActionAt
      const slug = toTestIdSlug(row.quote.name);

      const lastEmailStatus = row.quote.emailStatusDetail
        ?.sort((a, b) => {
          const timeA = dayjsFromApiUtc(a.utcTime || a.time).valueOf();
          const timeB = dayjsFromApiUtc(b.utcTime || b.time).valueOf();
          return timeB - timeA; // newest first (descending)
        })[0];
      return (
        <div
        data-testid={slug ? `carrierSelection-carrier-${slug}-status` : undefined}
        data-distribution-type={row.quote.distributionType ?? undefined}
        data-email-status={row.quote.emailStatus ?? undefined}
        data-carrier-quote-state={row.quote.carrierQuoteState ?? undefined}
        data-customer-quote-state={row.quote.customerQuoteState ?? undefined}
      >
          {carrierActionAtFlag && (
            <>
              {row.quote.distributionType === 'SPOT' && (
                <DescriptionDistributionStatus color={mapCarrierQuoteStateToColor(row.quote.carrierQuoteState)}>
                  {tr(`SpotQuoteTable.carrierQuoteState.${row.quote.carrierQuoteState}`, `${row.quote.carrierQuoteState}`)}
                </DescriptionDistributionStatus>
              )}{' '}
              {row.quote.distributionType === 'FIXED_PRICE' && (
                <DescriptionDistributionStatus color={mapCustomerQuoteStateToColor(row.quote.customerQuoteState)}>
                  {tr(`SpotQuoteTable.customerQuoteState.${row.quote.customerQuoteState}`, `${row.quote.customerQuoteState}`)}
                </DescriptionDistributionStatus>
              )}
              <div className="p-d-flex p-flex-wrap">
                <RowWithGap>
                  <div className="flex-col">
                    <div className="text-[14px] leading-[18px] font-normal text-content-gr4" data-testid={`carrierSelection-carrier-${slug}-timestamp`}>
                      {
                        loggedUser.defaultTimezoneId
                          ?  (stringDateTimeWithLocaleFromApiStringInTz(stateActionAt, loggedUser.defaultTimezoneId))
                          : (stringDateTimeWithLocaleFromApiStringInTz(stateActionAt, loggedUser.defaultTimezoneId))
                      }
                    </div>
                    <COTimezone dataTestId={`carrierSelection-carrier-${slug}-timezone`} timezoneId={loggedUser.defaultTimezoneId} />
                  </div>
                  <div className="tooltip" data-testid={slug ? `carrierSelection-carrier-${slug}-contact` : undefined}>
                    <img src={'/images/icons/ringil3/envelope.svg'} alt="info" className="p-button-text tooltip-button p-p-0" />
                    <div className="tooltip-container">
                      <div className="descrip-wrapper">
                        {row.quote.emailStatusDetail?.map((item, index) =>
                          DescriptionTooltip(index, item.userName, item.emailStatus,
                            item.utcTime ? dayjsFromApiUtcInTz(item.utcTime, timezoneId) : dayjsFromApiUtcInTz(item.time, timezoneId),
                            item.email, item.description, timezoneId),
                        )}
                      </div>
                    </div>
                  </div>
                </RowWithGap>
              </div>
            </>
          )}
          {(row.quote.emailStatus || lastEmailStatus) && !carrierActionAtFlag && (
            <>
              <DescriptionDistributionStatus color={mapToColor(lastEmailStatus.emailStatus || row.quote.emailStatus)}>
                {tr(`SpotQuoteDataTable.emailStatus.${lastEmailStatus.emailStatus || row.quote.emailStatus}`, 'SpotQuoteDataTable.emailStatus.${emailStatus}')}
              </DescriptionDistributionStatus>
              <div className="p-d-flex p-flex-wrap">
                <RowWithGap>
                  <div className="flex-col">
                    <div className="text-[14px] leading-[18px] font-normal text-content-gr4" data-testid={`carrierSelection-carrier-${slug}-timestamp`}>
                      {stringDateTimeWithLocaleFromApiStringInTz(lastEmailStatus.utcTime || row.quote.emailStatusCreatedAtUtc, loggedUser.defaultTimezoneId)}
                    </div>
                    <COTimezone dataTestId={`carrierSelection-carrier-${slug}-timezone`} timezoneId={loggedUser.defaultTimezoneId} />
                  </div>
                  <div className="tooltip" data-testid={slug ? `carrierSelection-carrier-${slug}-contact` : undefined}>
                  <img src={'/images/icons/ringil3/envelope.svg'} alt="info" className="p-button-text tooltip-button p-p-0" />
                  <div className="tooltip-container">
                      <div className="descrip-wrapper">
                        {row.quote.emailStatusDetail?.map((item, index) =>
                          DescriptionTooltip(index, item.userName, item.emailStatus,
                            item.utcTime ? dayjsFromApiUtcInTz(item.utcTime, timezoneId) : dayjsFromApiUtcInTz(item.time, timezoneId),
                            item.email, item.description, timezoneId),
                        )}
                      </div>
                    </div>
                  </div>
                </RowWithGap>
              </div>
            </>
          )}
        </div>
      );
    };

    const DescriptionTooltip = (index: number, name: string, status: string, dateTime: Dayjs, email: string, description: string, timezoneId: string) => {
      return (
        <DescriptionInner key={index}>
          <div className="descrip-detail__name">
            <strong>{name}</strong>
          </div>
          <div className="descrip-detail__name">
            <strong>{email}</strong>
          </div>
          <div className="p-d-flex p-jc-between">
            <small
              className="descrip-detail__status">{tr(`SpotQuoteDataTable.emailStatus.${status}`, 'SpotQuoteDataTable.emailStatus.${status}')}</small>
            <small className="descrip-detail__data">{formatDateTimeFromDayjsInTzWithLocale(dateTime, timezoneId)}</small>
          </div>
          <div className="p-d-flex p-jc-between">
            <small className="descrip-detail__status">{description}</small>
          </div>
        </DescriptionInner>
      );
    };

    const PriceCellWrapper = ({ children }: { children: React.ReactNode }) => {
      const filteredChildren = React.Children.toArray(children).filter(child => typeof child !== 'number');

      if (filteredChildren.length === 0) return null;
      return <PriceCell>{filteredChildren}</PriceCell>;
    };

    const renderPrice = (row: EnhancedQuote, toggleExpandedRow: (row: EnhancedQuote) => void): JSX.Element | null => {
      const ladenFactorUnit = tr(`QuoteTable.ladenFactorUnits`, `MU`);
      const slug = toTestIdSlug(row.quote.name);
      const dataTestId = slug ? `carrierSelection-carrier-${slug}-offeredPrice` : undefined;
      if (showPrices === false) return <div data-testid={dataTestId} />;
      if (!row.quote.distributionType || row.quote.distributionType !== 'SPOT') {
        if (
          !row.totalPrice ||
          !(
            row.quote.customerQuoteState === CustomerQuoteStateEnum.ACCEPTED_BY_CARRIER ||
            row.quote.customerQuoteState === CustomerQuoteStateEnum.ASSIGNED_MANUALLY_BY_CUSTOMER
          )
        ) {
          return <div data-testid={dataTestId} data-currency={row.quote.currency ?? undefined} />;
        }
        return (
          <SecuredArea id="spot-price" instance={row.quote.id} label="Price (non-SPOT)" description="The price the carrier quoted" visible={showPricesFlag}>
            <div
              data-testid={dataTestId}
              data-currency={row.quote.currency ?? undefined}
              data-total-price={row.totalPrice ?? undefined}
              data-price-per-km={showPricePerKm && row.quote.distance ? row.pricePerKm?.toFixed(2) : undefined}
            >
              <PriceCellWrapper>
                <COBadge variant={'filled'} type={'positive'}>
                  {[formatPrice(row.totalPrice, row.quote.currency), showPricePerKm && row.quote.distance ? `${row.pricePerKm.toFixed(2)} ${row.quote.currency}/km` : null]
                    .filter(t => t != null)
                    .join(' | ')}
                </COBadge>
              </PriceCellWrapper>
            </div>
          </SecuredArea>
        );
      }

      if (!row.totalPriceDto && (!row.quote.offeredPrice || row.quote.carrierQuoteState === CarrierQuoteStateEnum.REJECTED_BY_CARRIER)) {
        return <div data-testid={dataTestId} data-currency={row.quote.currency ?? undefined} />;
      }

    const timezoneId = loggedUser.defaultTimezoneId ??  row.quote.timezoneId ?? 'Europe/Prague';
    const tieBreakerTz = row.quote.tiebreakerTimezoneId ?? timezoneId;
    const isInTiebreak = row.quote.isTieBreaker === true;

      return (
        <SecuredArea id="spot-price" instance={row.quote.id} label="Price (SPOT)" description="The price the carrier quoted" visible={showPricesFlag}>
          <div
            data-testid={dataTestId}
            data-currency={row.quote.currency ?? undefined}
            data-offered-price={row.quote.offeredPrice ?? undefined}
            data-in-tiebreak={isInTiebreak ? 'true' : undefined}
          >
            <SecuredArea
              id="spot-tiebreak-progress" instance={slug}
              label="Tiebreak in progress note"
              visible={[{ type: 'data', label: 'isInTiebreak', satisfied: isInTiebreak }]}
            >
              <div className={'flex items-center gap-2 mb-2 text-content-gr4'}>
              <span className={'shrink-0'}>
                <SvgInfo />
              </span>
                <span>
                {row.quote.tiebreakerUtcEnd
                  ? tr(
                    'QuoteTable.tiebreakWaitingForNewOfferTill',
                    'Tiebreak in progress — waiting for new offer till {{-deadline}}',
                    { deadline: formatDateTimeFromDayjsInTzWithLocale(dayjsFromApiUtc(row.quote.tiebreakerUtcEnd), tieBreakerTz) },
                  )
                  : tr('QuoteTable.tiebreakWaitingForNewOffer', 'Tiebreak in progress — waiting for new offer')}
              </span>
              </div>
            </SecuredArea>
            <div>
              <PriceCellWrapper>
                <div className={isInTiebreak ? 'opacity-50' : ''}>
                  <SecuredArea
                    id="spot-price-per-hu" instance={slug}
                    label="Price per handling unit"
                    description="The quoted price per handling unit"
                    visible={[pricePerHuFlag, {
                      type: 'data',
                      label: 'offeredManipulationUnits',
                      satisfied: !!row.quote.offeredManipulationUnits,
                    }]}
                  >
                    <ManipulationUnitPriceTag
                      badgeType={'neutral'}
                      badgeVariant={'outlined'}
                      count={row.quote.offeredManipulationUnits}
                      pricePerUnitDto={row.pricePerManipulationUnitDto}
                      unit={ladenFactorUnit}
                    />
                  </SecuredArea>
                  <SecuredArea
                    id="spot-total-price" instance={slug}
                    label="Total price"
                    visible={[{ type: 'data', label: 'totalPriceDto', satisfied: !!row.totalPriceDto }]}
                  >
                    <PriceTag
                      badgeType={row.quote.carrierQuoteState === CarrierQuoteStateEnum.REJECTED_BY_CUSTOMER ? 'neutral' : 'positive'}
                      priceDto={row.totalPriceDto}
                      pricePerUnitDto={showPricePerKm ? row.pricePerKmDto : undefined}
                      unit={'km'}
                    />
                  </SecuredArea>
                </div>
                <Ringil3Note data-testid={`carrierSelection-carrier-${slug}-validTill`}>
                  {tr(`QuoteTable.offerValidTillDateWithTimeUtc`, `Offer valid till {{-dateWithTime}}`, {
                    dateWithTime: row.quote.utcEnd
                      ? formatDateTimeFromDayjsInTzWithLocale(dayjsFromApiUtc(row.quote.utcEnd), timezoneId)
                      : formatDateTimeFromDayjsInTzWithLocale(dayjsFromApiPrague(row.quote.validTill), timezoneId),
                  })}
                  <COTimezone dataTestId={`carrierSelection-carrier-${slug}-validTill-timezone`}
                              timezoneId={timezoneId} />
                </Ringil3Note>
                <SecuredArea
                  id="spot-carrier-reconfirmed" instance={slug}
                  label="Carrier reconfirmation note"
                  visible={[{
                    type: 'data',
                    label: 'isReconfirmedByCarrier',
                    satisfied: row.quote.isReconfirmedByCarrier,
                  }]}
                >
                  <Ringil3Note>
                    {tr(`QuoteTable.carrierReconfirmedAtEscaped`, `Offer confirmed by carrier at: {{-when}}`, {
                      when: row.quote.reconfirmedAtUtc
                        ? formatDateTimeFromDayjsInTzWithLocale(dayjsFromApiUtc(row.quote.reconfirmedAtUtc), timezoneId)
                        : formatDateTimeFromDayjsInTzWithLocale(dayjsFromApiPrague(row.quote.reconfirmedAt), timezoneId),
                    })}
                    <COTimezone timezoneId={timezoneId} />
                  </Ringil3Note>
                </SecuredArea>
                <SecuredArea
                  id="spot-ai-price-alert" instance={slug}
                  label="AI price alert"
                  description="AI check of the quoted price against expected prices"
                  visible={aiPriceFlag}
                >
                  <div
                    onClick={() => {
                      handleExpandRowToggle(row, ExpandedRowType.AiPriceAlert, toggleExpandedRow);
                    }}
                  >
                    <AiPriceAlertButton
                      severity={getDisplayableAiPriceAlert(row.quote.aiPriceAlert)?.aiPriceAlertSeverity}
                    />
                  </div>
                </SecuredArea>
              </PriceCellWrapper>
            </div>
          </div>
        </SecuredArea>
      );
    };

    const columnsConfig: ColumnConfig<EnhancedQuote>[] = [
      {
        id: 'carrier',
        header: 'SpotQuoteTable.carrier',
        headerDefault: 'Carrier',
        width: '21%',
        sortable: false,
        field: 'carrier',
        order: 1,
        render: renderName,
      },
      {
        id: 'state',
        header: 'SpotQuoteTable.state',
        headerDefault: 'State',
        width: '21%',
        sortable: false,
        order: 2,
        render: renderState,
      },
      {
        id: 'price',
        header: 'SpotQuoteTable.price',
        headerDefault: 'Offered Price',
        width: '21%',
        sortable: false,
        field: 'carrier',
        order: 3,
        render: renderPrice,
      },
      {
        id: 'additionalInfo',
        header: 'SpotQuoteTable.additionalInfo',
        headerDefault: 'Additional information',
        width: '21%',
        sortable: false,
        field: '',
        order: 4,
        render: renderAdditionalInfo,
      },
      {
        id: 'actions',
        header: 'SpotQuoteTable.action',
        headerDefault: 'Action',
        width: '16%',
        sortable: false,
        order: 5,
        render: renderActions,
      },
    ];

    const filtersConf: FilterConfig[] = [
      {
        filterType: FilterType.InputText,
        placeholder: tr(`SpotQuoteTable.carrier`, `Carrier name`),
        value: ['carrierName'],
      },
      {
        filterType: FilterType.Checkbox,
        placeholder: tr(`SpotQuoteTable.offerOnly`, `Only with offer`),
        value: ['onlyOffers'],
      },
    ];

    const isDistributionValidTillExpired = (): boolean => {
      if (!distributionUtcEnd) return false;
      return dayjsFromApiUtc(distributionUtcEnd).isBefore(dayjs());
    };

    const massActionConfig: MassActionConfig<EnhancedQuote>[] = [
      {
        label: tr('SpotQuoteTable.tiebreakCarrier', 'Tiebreak carrier'),
        secured: selected => ({
          id: 'spot-mass-action-tiebreak',
          label: 'Tiebreak carrier mass action',
          description: 'Ask the selected carriers for a tiebreak: another round of prices after the quote deadline',
          visible: [
            tiebreakFlag,
            { type: 'state', label: 'active distribution', satisfied: activeDistribution, description: 'Offered only in the active distribution', byDesign: true },
          ],
          editable: [
            { type: 'state', label: 'active distribution', satisfied: activeDistribution, description: 'Offered only in the active distribution', byDesign: true },
            { type: 'data', label: 'has selection', satisfied: selected.length >= 1 },
            // Deadline and capacity are states of the distribution, not "nothing to show": as `data` they would never reach the AI assistant.
            {
              type: 'state',
              label: 'distribution expired',
              satisfied: isDistributionValidTillExpired(),
              description: "Available only after the distribution's quote deadline has passed",
              byDesign: true,
            },
            {
              type: 'state',
              label: 'carrierQuoteSelectionEnabled',
              satisfied: carrierQuoteSelectionEnabled,
              description: 'With a laden factor set, carriers can be picked only while the shipment still has free handling units',
              byDesign: true,
            },
            {
              type: 'state',
              label: 'no accepted carrier in selection',
              satisfied: !selected.some(s => s.quote.carrierQuoteState === CarrierQuoteStateEnum.ACCEPTED_BY_CUSTOMER),
              description: "Not available when the selection includes a carrier whose quote is already accepted",
              byDesign: true,
            },
          ],
        }),
        onClick: () => {
          const selected = tableRef.current?.selectedObjects ?? [];
          if (selected.length < 1) return;
          setCarrierTiebreakSidebarData({
            distributionId,
            applicationId: selected[0].quote.expeditionApplicationId,
            carriers: selected.map(s => ({
              carrierCompanyId: s.quote.carrierId,
              carrierName: s.quote.name,
            })),
          });
        },
      },
    ];

    return (
      <>
        <RingilDataTable
          ref={tableRef}
          columnConfig={columnsConfig}
          filtersConf={filtersConf}
          loadDataFromApi={loadDataApi}
          filterName={'spot_quote_list'}
          rowExpansionTemplate={renderRowExpansion}
          massActionConfig={massActionConfig}
          enableReload={true}
          reloadLabel={tr('SpotQuoteTable.reloadOffers', 'Reload offers')}
        />
        <AcceptQuote
          onCompleted={() => {
            tableRef?.current?.reloadData();
            onChanged();
          }}
        />
        <RejectQuote
          onCompleted={() => {
            tableRef?.current?.reloadData();
            onChanged();
          }}
        />
        <CustomerChatComponent
          companyId={companyId}
          entityId={expeditionId}
          entityType={EntityTypeEnum.EXPEDITION}
          visible={isNotNullOrUndefined(selectedCarrierId)}
          selectedCarrierId={selectedCarrierId}
          onHide={() => setSelectedCarrierId(undefined)}
          readOnly={!editMessages}
          secured={{
            id: 'spot-chat-dialog',
            label: 'CustomerChatComponent (quotes)',
            description: 'Messages with a carrier about its quote',
            visible: showMessagesFlag,
            editable: editMessagesFlag,
          }}
        />
        <SchenkerQuoteAdditionalSidebar />
        <DHLQuoteAdditionalSidebar />
        <FedexQuoteAdditionalSidebar />
        <CarrierTiebreakSidebar
          onCompleted={() => {
            tableRef?.current?.clear();
            tableRef?.current?.reloadData();
            onChanged();
          }}
        />

      </>
    );
  }
);

const DescriptionInner = styled.div`
  display: flex;
  flex-direction: column;
`;

interface DescriptionDistributionStatusProps {
  color: string;
}

const DescriptionDistributionStatus = styled.div<DescriptionDistributionStatusProps>`
  font-weight: 500;
  font-size: 16px;
  line-height: 21px;
  ${props =>
    props.color &&
    css`
      color: ${props.color};
    `}
`;

const PriceCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const GreyLabel = styled.div`
  font-size: 12px;
  line-height: 16px;
  color: var(--ringil3-gr4);
  margin-top: -4px;
  margin-bottom: 4px;
`;

const TimeslotsSubtable = styled.div`
  width: 100%;
  margin-left: auto;
  margin-right: auto;
  padding: 0 1rem;

  table {
    width: 100%;
    border-collapse: collapse;
  }

  td {
    padding: 0.5rem;
    border-bottom: 1px solid #e9ecef;
  }

  .no-slots {
    color: var(--ringil3-gr4);
    font-size: 14px;
  }
`;

const StyledTimeslotHeader = styled.h4`
  font-family: 'DM Sans', sans-serif;
  font-weight: 500;
  color: #343333;
  margin-top: 0;
`;

export default SpotQuoteTable;
